import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ApiError } from '@app/api/ApiError';
import { get, type ApiData } from '@app/api/typed';
import { PENDING_REFETCH_MS } from './freshness';

/**
 * Hook de T19 (Mapa de acesso). Uma operação só: `getAccessMap` devolve nós,
 * arestas e divergências numa leitura.
 *
 * A leitura **nunca** falha por causa do executor: sem a leitura de acesso o
 * mapa vem montado com dados locais e `divergencias_indisponiveis: true`
 * (FR-105). Erro aqui é erro do próprio backend.
 */

export type AccessMap = ApiData<'/api/v1/access-map'>;
export type MapNode = AccessMap['nodes'][number];
export type MapEdge = AccessMap['edges'][number];
export type Divergences = AccessMap['divergences'];
export type KindIn = MapNode['kind'];
export type KindEdge = MapEdge['kind'];
export type Lens = AccessMap['lens'];
export type LensRule = Lens['rules'][number];
export type Reach = NonNullable<MapEdge['reach']>;

/** Um par claim=valor do modo avançado da lente. */
export interface ClaimPair {
  claim: string;
  value: string;
}

/**
 * A lente «Ver como» como a tela a guarda — o mesmo que vive na URL.
 *
 * `regra` e `claims` são exclusivas: ou o operador escolheu um perfil, ou
 * escreveu os pares à mão. Nenhuma das duas = sem lente.
 */
export interface LensSelection {
  regra: string | null;
  claims: ClaimPair[];
}

export const LENS_EMPTY: LensSelection = { regra: null, claims: [] };

/**
 * O `lens` ENQUANTO A PRIMEIRA LEITURA NÃO VOLTOU (05/09): a moldura da tela
 * renderiza na hora e a `LensBar` já aparece — desligada, sem regras — em vez de
 * nascer só com o grafo. É a forma do `lens` do servidor com tudo vazio; a
 * `LensBar` sabe que está `busy` e não lê «sem regras» como «não há regras».
 */
export const LENS_PENDING: Lens = {
  rules: [],
  active: false,
  kind: null,
  policy_id: null,
  label: null,
  claims: [],
  node_id: null,
  notice: null,
  unavailable: false,
  error_code: null,
  decisions: 0,
};

/** O literal que abre o modo avançado na URL. */
export const LENS_CLAIMS = 'claims';

export function lensActive(lens: LensSelection): boolean {
  return lens.regra !== null || lens.claims.length > 0;
}

/**
 * A lente na query string — o mesmo formato do link compartilhável e o mesmo
 * que o backend lê (`?view_as=<uuid|claims>&claim=<nome>=<valor>`).
 *
 * Um par por parâmetro `claim`, e não todos dentro de um só separados por `;`:
 * `URLSearchParams` codifica cada par sozinho, então um valor que contenha o
 * separador sobrevive à ida e à volta sem dupla codificação.
 */
export function lensParams(lens: LensSelection): URLSearchParams {
  const params = new URLSearchParams();
  if (lens.regra) {
    params.set('view_as', lens.regra);
    return params;
  }
  if (lens.claims.length === 0) return params;

  params.set('view_as', LENS_CLAIMS);
  for (const par of lens.claims) {
    params.append('claim', `${par.claim}=${par.value}`);
  }
  return params;
}

/** A leitura inversa de `paramsDaLente`: o que a URL diz que a lente é. */
export function lensFromParams(params: URLSearchParams): LensSelection {
  const raw = params.get('view_as');
  if (!raw) return LENS_EMPTY;
  if (raw !== LENS_CLAIMS) return { regra: raw, claims: [] };

  const claims: ClaimPair[] = [];
  for (const item of params.getAll('claim')) {
    // Separa no PRIMEIRO `=`: nome de claim não tem `=`, valor pode ter.
    const corte = item.indexOf('=');
    if (corte <= 0) continue;
    const claim = item.slice(0, corte).trim();
    const value = item.slice(corte + 1).trim();
    if (claim && value) claims.push({ claim, value });
  }
  return { regra: null, claims };
}

/**
 * `view_as` que o servidor recusaria: nem `claims`, nem um UUID.
 *
 * POR QUE A TELA CONFERE ISTO, SE O SERVIDOR JÁ CONFERE
 *
 *   Porque as duas recusas são respostas a perguntas diferentes. Para um
 *   cliente da API, `?view_as=xpto` é entrada inválida e 422 é a resposta certa
 *   — ela fica (`lensFrom` no handler). Para a TELA, o mesmo link é «o mapa,
 *   com uma lente que não dá para aplicar»: o mapa base não depende da lente e
 *   não tem por que sumir junto (DEF-12 — o 422 derrubava o mapa inteiro e
 *   mostrava «Revise os campos destacados» sem campo nenhum destacado, porque o
 *   campo é da query string e não existe formulário na tela).
 *
 *   Então a tela não manda o que sabe que será recusado: pede o mapa SEM lente
 *   e diz, inline, por que a lente não valeu. Mesma doutrina de FR-105 e da
 *   lente cega — o grafo sai inteiro, o que falhou fala por si.
 *
 * O modo `claims` não precisa disto: `lensFromParams` já descarta par inválido,
 * e `claims` sem nenhum par válido vira lente vazia, que é um pedido legítimo.
 */
export function lensMalformed(lens: LensSelection): boolean {
  return lens.regra !== null && !UUID.test(lens.regra);
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const accessMapKeys = {
  mapa: (lens: LensSelection = LENS_EMPTY) => ['access-map', lensParams(lens).toString()] as const,
};

/**
 * Quantas divergências o mapa traz, somadas as categorias — a contagem que a
 * aba «Divergências» de T19 mostra no rótulo. As listas vêm sempre desde o
 * aim-6ph.18, então o número sempre existe.
 *
 * Soma os valores em vez de listar as cinco chaves à mão: quando o contrato
 * ganhar mais uma categoria, a contagem já a inclui — e uma contagem que
 * ignora uma categoria é pior do que uma que não existe.
 */
export function countDivergences(mapping: AccessMap): number {
  return Object.values(mapping.divergences).reduce<number>(
    (total, lista) => total + (Array.isArray(lista) ? lista.length : 0),
    0,
  );
}

export function useAccessMap(
  lens: LensSelection = LENS_EMPTY,
): UseQueryResult<AccessMap, ApiError> {
  const query = lensParams(lens);
  return useQuery<AccessMap, ApiError>({
    // A lente entra na chave: trocar de perfil é outra leitura, e reaproveitar a
    // anterior mostraria o realce da regra que o operador acabou de deixar.
    queryKey: accessMapKeys.mapa(lens),
    // TROCAR A LENTE NÃO APAGA O MAPA (05/09). A leitura anterior fica na tela
    // como `placeholderData` enquanto a nova corre; a tela lê `isPlaceholderData`
    // + `isFetching` para dizer «Recalculando» e bloquear cliques no conteúdo. Sem
    // isto a chave nova nascia sem dado, o grafo sumia a cada troca de perfil e
    // voltava piscando.
    placeholderData: keepPreviousData,
    // O MAPA GUARDADO (mapa-acesso, 04/10): com mudança ainda não calculada
    // (`pending_since`), a leitura se repete até o servidor dizer que está em dia.
    // Em dia, nada se repete — a foto não muda sozinha.
    refetchInterval: (query) => (query.state.data?.pending_since ? PENDING_REFETCH_MS : false),
    queryFn: () =>
      get('/api/v1/access-map', {
        params: {
          view_as: query.get('view_as') ?? undefined,
          claim: query.getAll('claim'),
        },
        // `indexes: null` faz o axios repetir `claim=…&claim=…`. O padrão dele é
        // `claim[]=…`, que o backend lê como um parâmetro de outro nome — e a
        // lente chegaria lá sem claim nenhuma, em silêncio.
        config: { paramsSerializer: { indexes: null } },
      }),
  });
}
